import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, Save, X } from "lucide-react";

const UnsavedChangesModal = ({
  isOpen,
  onStay,
  onLeave,
  onSave,
  isSaving = false,
  title = "Leave without saving?",
  description = "Information you've entered may not be saved. Are you sure you want to leave this page?",
}) => {
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onStay?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onStay]);

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onStay}
        className="fixed inset-0 z-[100] backdrop-blur-sm bg-black/40"
        aria-hidden
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 16 }}
        transition={{ type: "spring", duration: 0.45 }}
        className="flex fixed inset-0 z-[101] justify-center items-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="unsaved-changes-title"
        aria-describedby="unsaved-changes-description"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="relative w-full max-w-md overflow-hidden bg-white rounded-3xl shadow-2xl">
          <div className="px-6 pt-6 pb-4">
            <button
              type="button"
              onClick={onStay}
              className="absolute top-4 right-4 p-1.5 rounded-full transition-colors hover:bg-gray-100"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>

            <div className="flex items-start gap-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-amber-50 text-amber-600 shrink-0">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div className="min-w-0 pt-1">
                <h2
                  id="unsaved-changes-title"
                  className="text-lg font-bold text-gray-900"
                >
                  {title}
                </h2>
                <p
                  id="unsaved-changes-description"
                  className="mt-2 text-sm leading-relaxed text-gray-600"
                >
                  {description}
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col-reverse gap-2 px-6 py-4 bg-gray-50 border-t border-gray-100 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onStay}
              disabled={isSaving}
              className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Stay on page
            </button>
            <button
              type="button"
              onClick={onLeave}
              disabled={isSaving}
              className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold text-gray-700 rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              Leave anyway
            </button>
            {onSave ? (
              <button
                type="button"
                onClick={onSave}
                disabled={isSaving}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white rounded-xl bg-[#FF6C6C] shadow-md shadow-rose-200/70 hover:bg-[#e65e5e] transition-colors disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {isSaving ? "Saving..." : "Save & leave"}
              </button>
            ) : null}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default UnsavedChangesModal;
